"use client";

import { useMemo } from "react";
import Link from "next/link";
import { School } from "lucide-react";
import { cn } from "@/lib/utils";
import { PRODUCTS, PRODUCTS_LIST, type ProductId } from "@/lib/constants/products";
import { APP_THEMES, GLOBAL_PRIMARY_HSL, type AppThemeKey } from "@/lib/constants/themes";
import { useHomeAttention } from "@/lib/hooks/use-home-attention";
import { AttentionPanel } from "@/components/home/AttentionPanel";
import { HubSearch } from "@/components/home/HubSearch";
import { withAssistantName } from "@/lib/ai/assistant/mascot";
import { useAssistantName } from "@/lib/hooks/use-assistant-name";
import { FREE_TRIAL_DAYS } from "@/lib/utils/subscriptionUtils";

/** Per-app CSS background gradient hex stops, derived from APP_THEMES (single source) */
// eslint-disable-next-line react-refresh/only-export-components
export const APP_BG_COLORS: Record<AppThemeKey, string> = Object.fromEntries(
  (Object.entries(APP_THEMES) as [AppThemeKey, (typeof APP_THEMES)[AppThemeKey]][]).map(
    ([key, theme]) => [key, theme.bgColors],
  ),
) as Record<AppThemeKey, string>;

// eslint-disable-next-line react-refresh/only-export-components
export function getPrimaryColor(productId: ProductId): string {
  const hsl = APP_THEMES[productId]?.cssVars?.['--primary'] ?? GLOBAL_PRIMARY_HSL;
  return `hsl(${hsl})`;
}

/**
 * iOS-home-screen tile: a white squircle floating on the page gradient with a
 * layered shadow. The app's brand color is held back for hover (a colored glow
 * via --tile-accent) so a dozen tiles read as one grid instead of a rainbow.
 */
const TILE_BASE =
  "relative w-24 h-24 md:w-28 md:h-28 rounded-[22px] md:rounded-[26px] bg-white overflow-hidden transition-all duration-300";

/** Tiles you own: clean, no ring beyond a hairline that keeps white off white. */
const TILE_OWNED = "ring-1 ring-black/[0.06] hover:-translate-y-1 active:scale-[0.97] cursor-pointer";

/** Tiles you don't own yet read as empty slots — dashed outline, muted art. */
const TILE_LOCKED =
  "border-2 border-dashed opacity-85 hover:opacity-100 hover:-translate-y-1 active:scale-[0.97] cursor-pointer";

/** Grid columns as explicit widths, so a short final row centers instead of hanging left. */
const TILE_COL =
  "w-[calc((100%-2rem)/3)] sm:w-[calc((100%-3rem)/4)] md:w-[calc((100%-6rem)/5)]";

const apps = PRODUCTS_LIST.map(p => ({
  id: p.id,
  name: p.name,
  description: p.description,
  tagline: p.tagline,
  icon: p.icon,
  href: p.route,
  color: p.colors.gradient,
  bgLight: p.colors.accentBg,
  iconColor: p.colors.accent,
  image: p.image,
  primaryColor: getPrimaryColor(p.id),
  status: p.status,
}));

interface SubData {
  subscriptions: {
    id: string;
    product_id: string;
    status: string;
    max_schools: number;
    stripe_subscription_id: string | null;
    interval: string | null;
    linked_schools?: { id: string }[];
    trial_ends_at?: string | null;
  }[];
}

interface EditModeConfig {
  currentHubs: Record<string, number>;
  interval: 'monthly' | 'annual';
}

interface AppGridProps {
  displayName: string;
  isOwner: boolean;
  isAdmin: boolean;
  isPlatformAdmin: boolean;
  isBetaTester: boolean;
  accessMap: Record<string, boolean>;
  documentsRole: string | null;
  evalTeacherRole: string | null;
  hasAnyAccess: boolean;
  activeSchool: { id: string; name: string | null } | null;
  trialProducts: Set<string>;
  isAnyTrialing: boolean;
  expiredProducts: Set<string>;
  editModeConfig: EditModeConfig | null;
  subData: SubData | undefined;
  hasReferral: boolean;
  referrerName: string | null;
  hoveredApp: string | null;
  setHoveredApp: (id: string | null) => void;
  setHubPickerOpen: (open: boolean) => void;
  setHubPickerPreSelected: (id: ProductId | undefined) => void;
  setHubPickerOverrides: (overrides: Record<string, number> | undefined) => void;
  setAddHubTarget: (target: { id: string; name: string; href: string } | null) => void;
}

export function AppGrid({
  displayName,
  isOwner,
  isAdmin,
  isPlatformAdmin,
  isBetaTester,
  accessMap,
  documentsRole,
  evalTeacherRole,
  hasAnyAccess,
  activeSchool,
  trialProducts,
  isAnyTrialing,
  expiredProducts,
  editModeConfig,
  subData,
  hasReferral,
  referrerName,
  hoveredApp: _hoveredApp,
  setHoveredApp,
  setHubPickerOpen,
  setHubPickerPreSelected,
  setHubPickerOverrides,
  setAddHubTarget,
}: AppGridProps) {
  // A school can rename the assistant and give it its own face. The tile is where a director
  // first meets it, so an "Ask Eddy" tile leading to a hub that calls itself Johnny is the
  // rename failing at the front door.
  const { name: assistantName, imageUrl: mascotUrl } = useAssistantName();

  // Override app card labels / entry routes for teacher-role users so they land
  // on the right place directly (no dashboard flash before a layout redirect).
  const displayApps = apps.map(app => {
    if (app.id === 'resources') {
      app = {
        ...app,
        name: withAssistantName(app.name, assistantName),
        image: mascotUrl ?? app.image,
      };
    }
    if (app.id === 'documents' && documentsRole === 'teacher') {
      return { ...app, name: 'PTO Hub', description: 'View your PTO balance and submit time-off requests.', tagline: 'Your PTO balance & requests' };
    }
    if (app.id === 'eval_teacher' && !isAdmin && !isPlatformAdmin && evalTeacherRole === 'teacher') {
      return { ...app, href: '/eval-teacher/my-profile' };
    }
    return app;
  });

  // True only for brand-new users who have never subscribed or trialed
  const isNewUser = !hasAnyAccess && (subData?.subscriptions || []).length === 0

  // The unified Calendar only earns its tile when it aggregates 2+ calendar-bearing hubs —
  // with one (or none) it just mirrors that hub's own calendar and adds nothing.
  const calendarHubCount = (['directors', 'documents', 'compliance', 'staffing'] as const)
    .filter((id) => accessMap[id]).length
  const showCalendarTile = calendarHubCount >= 2

  // The attention list only has anything to say once a calendar-bearing hub is in play.
  // Owners/admins can approve PTO, so they alone get the pending-requests row.
  const attention = useHomeAttention({
    enabled: calendarHubCount >= 1 || isPlatformAdmin,
    canApprovePto: isOwner || isAdmin || isPlatformAdmin,
  })

  // Trial days remaining
  const trialDaysRemaining = useMemo(() => {
    const trialSub = (subData?.subscriptions || []).find(s => s.status === 'trialing' && s.trial_ends_at)
    if (!trialSub?.trial_ends_at) return null
    const endMs = new Date(trialSub.trial_ends_at).getTime()
    // eslint-disable-next-line react-hooks/purity -- Date.now() in useMemo is intentional for computing days remaining
    const remaining = endMs - Date.now()
    return Math.max(0, Math.ceil(remaining / (1000 * 60 * 60 * 24)))
  }, [subData])

  // Owners and platform admins see unsubscribed apps as available to subscribe
  // Non-owners only see apps they have access to
  const accessibleApps = displayApps
    .filter(app => app.status !== 'hidden')
    .filter(app => {
      if (app.status === 'live') return true
      // Beta hubs are visible to admins/beta testers (admins only as a locked teaser). Coming soon: admin-preview only.
      if (app.status === 'beta') return isAdmin || isPlatformAdmin || isBetaTester
      if (app.status === 'coming_soon') return isAdmin || isPlatformAdmin
      return isPlatformAdmin
    })
    .map(app => {
      // Beta hubs unlock only for platform admins / tagged beta testers; everyone else gets the locked Coming Soon teaser.
      const betaUnlocked = isPlatformAdmin || isBetaTester
      return {
        ...app,
        comingSoon: (app.status === 'coming_soon' && !isPlatformAdmin) || (app.status === 'beta' && !betaUnlocked),
        betaPreview: app.status === 'beta' && betaUnlocked,
      }
    })
    .filter(app => accessMap[app.id] || isAdmin || isPlatformAdmin || app.status === 'beta' || expiredProducts.has(app.id))
    .sort((a, b) => {
      const aOrder = a.comingSoon ? 2 : accessMap[a.id] ? 0 : 1;
      const bOrder = b.comingSoon ? 2 : accessMap[b.id] ? 0 : 1;
      return aOrder - bOrder;
    });

  return (
    <>
      {/* Greeting — hidden when showing welcome letter */}
      {!(isOwner && !hasAnyAccess) && (
        <div className="text-center mb-6 md:mb-10">
          <p
            className="text-4xl md:text-5xl text-gray-900 mb-1"
            style={{ fontFamily: "Alex Brush, cursive" }}
          >
            Hey {displayName ? displayName.split(' ')[0].charAt(0).toUpperCase() + displayName.split(' ')[0].slice(1) : ''}!
          </p>
          {/* Trial countdown — owner only */}
          {isOwner && isAnyTrialing && trialDaysRemaining != null && trialDaysRemaining > 0 && (
            <p className="text-sm text-gray-500 mt-1">
              Trial ends in {trialDaysRemaining} day{trialDaysRemaining !== 1 ? 's' : ''}
            </p>
          )}
        </div>
      )}

      {/* Welcome letter for owners with no subscriptions */}
      {isOwner && !hasAnyAccess && accessibleApps.length > 0 && (
        <div className="mb-8 md:mb-10 max-w-2xl mx-auto rounded-sm bg-white shadow-[0_2px_15px_rgba(0,0,0,0.08)] px-8 py-8 md:px-14 md:py-12 relative"
          style={{ backgroundImage: 'linear-gradient(to bottom, transparent 95%, #f5f0e8)' }}
        >
          {/* Decorative top border */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-400 via-orange-300 to-purple-400" />

          {/* Salutation */}
          <p
            className="text-4xl md:text-5xl text-gray-900 mb-4"
            style={{ fontFamily: "Alex Brush, cursive" }}
          >
            Hey {displayName ? displayName.split(' ')[0].charAt(0).toUpperCase() + displayName.split(' ')[0].slice(1) : ''}!
          </p>

          <h3
            className="text-lg md:text-xl font-bold text-gray-800 mb-5"
            style={{ fontFamily: 'Cormorant Garamond, serif', letterSpacing: '0.02em' }}
          >
            Welcome to The Hub
          </h3>

          <div className="space-y-4 text-[15px] text-gray-700 leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
            <p>
              I built The Hub because I know firsthand how overwhelming it is to run a childcare program. You probably already have tools for parent communication and daily tracking &mdash; and those are great for that. But what about everything behind the scenes? Compliance deadlines, staff onboarding, budgets, evaluations, documentation &mdash; the inner webs of childcare that actually keep your program running?
            </p>
            <p>
              That&apos;s exactly what The Hub is for. Think of it as your internal command center &mdash; a set of connected hubs, each built for a specific part of managing your program from the inside out. No parent-facing features, just the back-office tools directors actually need.
            </p>
            <p>
              Pick the hubs your school needs below &mdash; start with one or bundle a few. You can always add more as your needs grow.
            </p>

            <p className="mt-6 text-gray-600" style={{ fontFamily: 'Alex Brush, cursive', fontSize: '1.5rem' }}>
              Halle Melendez
            </p>
            <p className="text-xs text-gray-500 -mt-3" style={{ fontFamily: 'Georgia, serif' }}>
              Founder, Admin4You LLC
            </p>
          </div>

          {/* CTA row */}
          <div className="flex flex-col sm:flex-row items-center gap-3 mt-8 pt-6 border-t border-gray-100">
            <button
              onClick={() => {
                setHubPickerPreSelected(undefined);
                setHubPickerOpen(true);
              }}
              className="px-6 py-2.5 text-sm font-semibold text-white rounded-lg bg-gradient-to-r from-purple-600 to-orange-500 hover:shadow-lg transition-all active:scale-95"
            >
              Choose Your Hubs
            </button>
            <Link
              href="/settings/schools"
              className="px-6 py-2.5 text-sm font-semibold text-gray-700 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 hover:shadow transition-all active:scale-95"
            >
              Set Up Schools
            </Link>
            <Link
              href="/pricing"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors underline underline-offset-4"
            >
              See pricing
            </Link>
          </div>

          {/* Referral discount banner */}
          {hasReferral && (
            <div className="mt-4 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 text-center">
              <p className="text-sm font-medium text-emerald-800">
                {referrerName ? `${referrerName} referred you` : "You\u2019ve been referred"}! Get <span className="font-bold">20% off</span> your first month when you subscribe.
              </p>
            </div>
          )}
        </div>
      )}

      {/* App Icons Grid */}
      {accessibleApps.length > 0 ? (
        <>
          {!(isOwner && !hasAnyAccess) && (
            <HubSearch
              onSelectLockedHub={(id) => {
                setHubPickerOverrides(undefined);
                setHubPickerPreSelected(id);
                setHubPickerOpen(true);
              }}
            />
          )}
          <div className="flex flex-wrap justify-center gap-4 md:gap-6">
            {accessibleApps.map((app, index) => {
              const hasAccess = accessMap[app.id];
              const needsSubscription = !hasAccess && !app.comingSoon && !app.betaPreview && isOwner && !isPlatformAdmin;
              const isOnTrial = hasAccess && trialProducts.has(app.id);
              const isExpired = !hasAccess && expiredProducts.has(app.id);
              const isLinkable = (() => {
                // During trial all schools are auto-linked — skip linkable check
                if (isAnyTrialing) return false;
                if (!needsSubscription || !editModeConfig?.currentHubs[app.id]) return false;
                const subs = subData?.subscriptions || [];
                const hubSub = subs.find(s => s.product_id === app.id && ['active', 'trialing', 'cancelling'].includes(s.status));
                const linkedCount = hubSub?.linked_schools?.length ?? 0;
                const maxSchools = hubSub?.max_schools ?? 3;
                return linkedCount < maxSchools;
              })();
              const tileContent = (
                <div className="flex flex-col items-center w-full max-w-[140px] md:max-w-[160px] group/tile">
                  {/* Border wrapper */}
                  <div
                    data-app-tile
                    data-tile-state={app.comingSoon ? 'soon' : needsSubscription || isExpired ? 'locked' : 'owned'}
                    className={cn(
                      TILE_BASE,
                      app.comingSoon
                        ? "border-2 border-dashed border-gray-300 opacity-60 grayscale-[40%] cursor-default"
                        : needsSubscription || isExpired
                          ? TILE_LOCKED
                          : TILE_OWNED,
                    )}
                    style={{
                      ['--tile-accent' as string]: app.primaryColor,
                    } as React.CSSProperties}
                  >
                      {(() => {
                        const badgeLabel = app.comingSoon ? 'Coming Soon'
                          : app.betaPreview ? 'Beta'
                          : isExpired ? 'Expired'
                          : isOnTrial ? 'Trial'
                          : needsSubscription ? (isLinkable ? 'Link' : isNewUser ? 'Try Free' : isAnyTrialing ? 'Add to Trial' : 'Subscribe')
                          : null;
                        if (!badgeLabel) return null;
                        const bgColor = app.comingSoon
                          ? 'rgba(107,114,128,0.85)'
                          : app.betaPreview
                            ? 'rgba(124,58,237,0.9)' // violet-600
                            : isExpired
                              ? 'rgba(220,38,38,0.9)' // red-600
                              : app.primaryColor;
                        return (
                          <div
                            className="absolute top-1.5 right-1.5 z-10 text-white text-[9px] font-semibold px-1.5 py-0.5 rounded-full backdrop-blur-sm"
                            style={{ backgroundColor: bgColor }}
                          >
                            {badgeLabel}
                          </div>
                        );
                      })()}
                      <img
                        src={app.image}
                        alt={app.name}
                        className={cn(
                          "w-full h-full object-contain transition-transform duration-300 group-hover/tile:scale-110",
                          (needsSubscription || isExpired) && "opacity-70"
                        )}
                      />
                  </div>
                  {/* Name + description below */}
                  <h2 className="text-sm font-semibold text-foreground text-center mt-2.5 leading-tight">
                    {app.name}
                  </h2>
                  <p className="text-[11px] text-muted-foreground text-center line-clamp-2 mt-0.5 leading-snug">
                    {app.tagline}
                  </p>
                  {/* Only states that actually differ per tile get a line here. The price used to
                      be the fallback, which meant "Starting at $19.99/mo" repeated on every locked
                      tile — eleven times on a full grid, for a number that is identical everywhere
                      and stops being true the moment graduated pricing kicks in. "See pricing" in
                      the header above says it once, correctly. */}
                  {(needsSubscription || isExpired) && !isLinkable && (() => {
                    const note = isExpired
                      ? 'Subscription expired — Renew'
                      : isNewUser
                        ? `${FREE_TRIAL_DAYS}-day free trial`
                        : isAnyTrialing
                          ? 'Add to trial'
                          : null;
                    if (!note) return null;
                    return (
                      <p className={cn("text-[10px] font-medium mt-1", isExpired ? "text-red-600" : "text-muted-foreground")}>
                        {note}
                      </p>
                    );
                  })()}
                </div>
              );

              const hoverProps = {
                onMouseEnter: () => setHoveredApp(app.id),
                onMouseLeave: () => setHoveredApp(null),
                className: cn("app-tile-in flex justify-center", TILE_COL),
                style: { animationDelay: `${index * 35}ms` } as React.CSSProperties,
              };

              if (app.comingSoon) {
                return <div key={app.id} {...hoverProps}>{tileContent}</div>;
              }

              if (needsSubscription || isExpired) {
                return (
                  <div
                    key={app.id}
                    {...hoverProps}
                    onClick={() => {
                      if (isLinkable) {
                        setAddHubTarget({ id: app.id, name: app.name, href: app.href });
                        return;
                      }
                      if (editModeConfig?.currentHubs[app.id] != null) {
                        const maxSchools = editModeConfig.currentHubs[app.id];
                        const spt = PRODUCTS[app.id as ProductId]?.schoolsPerTier ?? 1;
                        setHubPickerOverrides({ [app.id]: maxSchools + spt });
                        setHubPickerPreSelected(undefined);
                      } else {
                        setHubPickerOverrides(undefined);
                        setHubPickerPreSelected(app.id);
                      }
                      setHubPickerOpen(true);
                    }}
                  >
                    {tileContent}
                  </div>
                );
              }

              return (
                <Link key={app.id} href={app.href} {...hoverProps}>{tileContent}</Link>
              );
            })}

            {/* Calendar — cross-hub aggregate view; shown only when it spans 2+ calendar hubs */}
            {showCalendarTile && (
              <Link
                href="/calendar"
                className={cn("app-tile-in flex justify-center", TILE_COL)}
                style={{ animationDelay: `${accessibleApps.length * 35}ms` }}
                onMouseEnter={() => setHoveredApp('calendar')}
                onMouseLeave={() => setHoveredApp(null)}
              >
                <div className="flex flex-col items-center w-full max-w-[140px] md:max-w-[160px] group/tile">
                  <div
                    data-app-tile
                    data-tile-state="owned"
                    className={cn(TILE_BASE, TILE_OWNED)}
                    style={{ ['--tile-accent' as string]: "#f43f5e" } as React.CSSProperties}
                  >
                    <img
                      src="/hub-icons/calendarhub-image.webp"
                      alt="Calendar"
                      className="w-full h-full object-contain transition-transform duration-300 group-hover/tile:scale-110"
                    />
                  </div>
                  <h2 className="text-sm font-semibold text-foreground text-center mt-2.5 leading-tight">
                    Calendar
                  </h2>
                  <p className="text-[11px] text-muted-foreground text-center line-clamp-2 mt-0.5 leading-snug">
                    Every hub date in one view
                  </p>
                </div>
              </Link>
            )}
          </div>
          {!(isOwner && !hasAnyAccess) && (
            <AttentionPanel {...attention} showCalendarLink={showCalendarTile} />
          )}
        </>
      ) : activeSchool ? (
        isOwner ? (
          /* Owner with no subscriptions — friendly onboarding CTA */
          <div className="text-center py-10">
            <h3
              className="text-2xl font-semibold text-gray-800 mb-2"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Get Started with Your First Hub
            </h3>
            <p className="text-sm text-gray-500 max-w-md mx-auto mb-8">
              Choose the tools your school needs. Add more anytime &mdash; the more hubs you use, the less each one costs.
            </p>

            <button
              onClick={() => {
                setHubPickerPreSelected(undefined);
                setHubPickerOpen(true);
              }}
              className="inline-block mb-6 px-6 py-2.5 text-sm font-semibold text-white rounded-lg bg-gradient-to-r from-purple-600 to-orange-500 hover:shadow-lg transition-all active:scale-95"
            >
              Choose Your Hubs
            </button>

            <div className="grid gap-4 md:grid-cols-2 max-w-2xl mx-auto">
              {displayApps.map(app => {
                const Icon = app.icon;
                return (
                  <div
                    key={app.id}
                    onClick={() => {
                      setHubPickerPreSelected(app.id);
                      setHubPickerOpen(true);
                    }}
                    className={cn(
                      "flex items-center gap-4 rounded-2xl border-2 border-dashed bg-white/80 p-4",
                      "hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer",
                    )}
                    style={{ borderColor: app.primaryColor }}
                  >
                    <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center shrink-0", app.bgLight)}>
                      <Icon className={cn("h-6 w-6", app.iconColor)} />
                    </div>
                    <div className="text-left min-w-0">
                      <h4 className="text-sm font-semibold text-gray-900">{app.name}</h4>
                      {/* Same reason as the grid above: one price, repeated per card, with a
                          "See how pricing works" link directly beneath it. */}
                      <p className="text-xs text-muted-foreground line-clamp-2">{app.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <Link
              href="/pricing"
              className="inline-block mt-6 text-sm text-muted-foreground hover:text-foreground transition-colors underline underline-offset-4"
            >
              See how pricing works
            </Link>
          </div>
        ) : (
          /* Non-owner member with no access */
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/60 mb-4">
              <School className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              No apps available yet
            </h3>
            <p className="text-sm text-gray-500 max-w-sm mx-auto mb-6">
              Your school admin hasn&apos;t set up any apps yet. Here&apos;s what&apos;s available:
            </p>
            <div className="grid gap-3 max-w-sm mx-auto text-left">
              {displayApps.map(app => {
                const Icon = app.icon;
                return (
                  <div key={app.id} className="flex items-center gap-3 rounded-lg bg-white/60 px-3 py-2.5">
                    <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center shrink-0", app.bgLight)}>
                      <Icon className={cn("h-4 w-4", app.iconColor)} />
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-700">{app.name}</span>
                      <p className="text-xs text-muted-foreground">{app.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-sm text-gray-500 mt-6">
              Ask your school admin to subscribe and grant you access.
            </p>
          </div>
        )
      ) : (
        /* No school at all */
        <div className="text-center py-16">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/60 mb-4">
            <School className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-700 mb-2">
            No apps available
          </h3>
          <p className="text-sm text-gray-500 max-w-sm mx-auto">
            You&apos;re not a member of any school yet. Contact your school admin for an invite.
          </p>
        </div>
      )}
    </>
  );
}
